import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Fade } from 'react-reveal';
import SearchBar from '../Components/SearchBar/SearchBar';
import { performComprehensiveSearch, highlightSearchTerms, getSnippet } from '../utils/searchUtils';
import './SearchResults.css';

function SearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const resultsPerPage = 10;

  // Get unique categories from results
  const categories = ['all', ...new Set(results.map(r => r.category))];

  // Perform search when query changes
  useEffect(() => {
    if (query && query.trim().length >= 2) {
      setIsLoading(true);
      const searchResults = performComprehensiveSearch(query);
      setResults(searchResults);
      setFilteredResults(searchResults);
      setSelectedCategory('all');
      setCurrentPage(1);
      setIsLoading(false);
    } else {
      setResults([]);
      setFilteredResults([]);
    }
  }, [query]);

  // Filter results by category
  useEffect(() => {
    if (selectedCategory === 'all') {
      setFilteredResults(results);
    } else {
      setFilteredResults(results.filter(r => r.category === selectedCategory));
    }
    setCurrentPage(1);
  }, [selectedCategory, results]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredResults.length / resultsPerPage);
  const startIndex = (currentPage - 1) * resultsPerPage;
  const endIndex = startIndex + resultsPerPage;
  const currentResults = filteredResults.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  const handleResultClick = (path) => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  };

  return (
    <div className="search-results-page">
      <div className="search-results-header-section">
        <div className="container">
          <Fade top>
            <div className="search-logo-section">
              <Link to="/" onClick={() => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })}>
                <h1 className="search-logo">Salaam Takaful</h1>
              </Link>
            </div>
            <div className="search-bar-section">
              <SearchBar onSearchPage={true} />
            </div>
            {query && query.trim().length >= 2 && (
              <div className="search-query-display">
                <p>Search results for: <strong>"{query}"</strong></p>
              </div>
            )}
          </Fade>
        </div>
      </div>

      <div className="container search-results-content">
        {query && query.trim().length >= 2 ? (
          <>
            {isLoading ? (
              <div className="search-loading">
                <div className="loading-spinner"></div>
                <p>Searching...</p>
              </div>
            ) : (
              <>
                {/* Results Stats */}
                <div className="search-stats">
                  <p>
                    About <strong>{filteredResults.length}</strong> results
                    {query && ` for "${query}"`} 
                    {filteredResults.length > 0 && (
                      <span> ({(currentPage - 1) * resultsPerPage + 1} - {Math.min(currentPage * resultsPerPage, filteredResults.length)} of {filteredResults.length})</span>
                    )}
                  </p>
                </div>

                {/* Category Filters */}
                {categories.length > 1 && (
                  <div className="search-filters">
                    <span className="filter-label">Filter by:</span>
                    {categories.map((category) => (
                      <button
                        key={category}
                        className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                        onClick={() => setSelectedCategory(category)}
                      >
                        {category === 'all' ? 'All Results' : category}
                      </button>
                    ))}
                  </div>
                )}

                {/* Search Results */}
                {currentResults.length > 0 ? (
                  <div className="search-results-list">
                    {currentResults.map((result, index) => (
                      <Fade bottom key={index} delay={index * 50}>
                        <div className="search-result-card">
                          <div className="result-header">
                            <div className="result-type-badge">
                              {result.type === 'page' && '📄 Page'}
                              {result.type === 'news' && '📰 News'}
                              {result.type === 'article' && '📝 Article'}
                              {result.type === 'product' && '🛡️ Product'}
                            </div>
                            <div className="result-category">{result.category}</div>
                          </div>
                          <Link
                            to={result.path}
                            className="result-title-link"
                            onClick={() => handleResultClick(result.path)}
                            dangerouslySetInnerHTML={{
                              __html: highlightSearchTerms(result.title, query)
                            }}
                          />
                          <div className="result-url">{window.location.origin}{result.path}</div>
                          {result.snippet && (
                            <div
                              className="result-snippet"
                              dangerouslySetInnerHTML={{
                                __html: highlightSearchTerms(
                                  getSnippet(result.snippet, query, 250),
                                  query
                                )
                              }}
                            />
                          )}
                          {result.date && (
                            <div className="result-date">{result.date}</div>
                          )}
                        </div>
                      </Fade>
                    ))}
                  </div>
                ) : (
                  <div className="no-results">
                    <div className="no-results-icon">🔍</div>
                    <h2>No results found</h2>
                    <p>Try different keywords or check your spelling</p>
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="search-pagination">
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      ← Previous
                    </button>
                    <div className="pagination-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(page => {
                          // Show first, last, current, and pages around current
                          return (
                            page === 1 ||
                            page === totalPages ||
                            (page >= currentPage - 2 && page <= currentPage + 2)
                          );
                        })
                        .map((page, index, array) => {
                          // Add ellipsis
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;
                          
                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && <span className="pagination-ellipsis">...</span>}
                              <button
                                className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                                onClick={() => handlePageChange(page)}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>
                    <button
                      className="pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next →
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="search-empty-state">
            <div className="empty-state-icon">🔍</div>
            <h2>Search Salaam Takaful</h2>
            <p>Enter a search query above to find pages, news, articles, and products</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchResults;
